import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {Store} from '@ngrx/store';
import {distinctUntilChanged, finalize, take, takeUntil} from 'rxjs/operators';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {getSelectedCustomer} from "@store/selectors/user.selector";
import {AppState} from "@store/state/app.state";
import {OrdersService} from "@iw-shop/order/services/order.service";
import {ArticleService} from "@iw-shop/article/services/article.service";
import {setShoppingCartArticles} from "@store/actions/shopping-cart.action";
import {ApiOrder} from "@api/orders";
import {NumberFormat} from "@iw-shop/core//functions/number-format";
import {PageEvent} from "@iw-shop/core//components/paginator/paginator.component";
import {UserService} from "@iw-shop/user/services/user.service";

interface OrderExt extends ApiOrder {
    loadingConfirmation?: boolean;
    loadingPutOrderToCart?: boolean;
}

@Component({
    selector: 'iw-shop-customer-orders',
    standalone: false,
    templateUrl: './customer-orders.component.html',
    styleUrls: ['./customer-orders.component.scss']
})


export class CustomerOrdersComponent implements OnInit, OnDestroy {
    destroy$: Subject<boolean> = new Subject<boolean>();

    customerId?: string;

    orders: OrderExt[] = [];
    loading: boolean = true;

    currency: 'EUR' | 'CHF' = 'EUR';

    start: number = 0;
    limit: number = 40;
    length: number = 0;


    orderConfirmation?: SafeResourceUrl;
    orderStatus: { name: string, translation: string, color: string }[] = [
        {
            name: 'open',
            translation: 'Offen',
            color: ''
        },
        {
            name: 'canceled',
            translation: 'Storniert',
            color: 'red'
        },
        {
            name: 'progressStarted',
            translation: 'Offen',
            color: 'white'
        },
        {
            name: 'inProgress',
            translation: 'In Bearbeitung',
            color: 'white'
        },
        {
            name: 'inDelivery',
            translation: 'In Zustellung',
            color: 'orange'
        },
        {
            name: 'finished',
            translation: 'Ausgeliefert',
            color: 'green'
        }
    ];

    constructor(private router: Router, private store: Store<AppState>, public ordersService: OrdersService, private sanitizer: DomSanitizer, public articleService: ArticleService, public userService: UserService) {
        this.store.select(state => state.config)
            .pipe(take(1))
            .subscribe(
                c => {
                    this.currency = c.currency;
                }
            )
    }

    ngOnInit(): void {
        this.store.select(getSelectedCustomer)
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged()
            )
            .subscribe(
                res => {
                    if (res?.id) {
                        this.customerId = res.id;
                        this.loadOrders();
                    }
                }
            )
    }

    changePage(event: PageEvent) {
        this.start = event.start;
        this.limit = event.limit;
        window.scroll(0, 0);
        this.loadOrders();
    }

    loadOrders() {
        this.loading = true;
        if (this.customerId) {
            this.ordersService.get(
                {
                    filters: [{fields: ['customerId'], values: [this.customerId]}],
                    orders: {
                        start: this.start,
                        limit: this.limit,
                        expectedData: {
                            dateCreated: 1,
                            sum: 1,
                            items: 1,
                            priceMode: 1,
                            status: 1,
                            count: 1
                        }
                    }
                }
            )
                .pipe(
                    finalize(() => this.loading = false)
                )
                .subscribe(
                    r => {
                        if (r.result?.orders?.data && r.result.orders.count) {
                            this.length = r.result.orders.count.filtered;
                            this.orders = r.result.orders.data;
                        }
                    }
                )
        }

    }

    getOrderStatusColor(status: string): string | null {
        const item = this.orderStatus.filter(orderStatus => orderStatus.name == status);
        if (item.length == 1) {
            return item[0].color;
        }
        return null;
    }

    getOrderStatusTranslation(status: string): string | null {
        const item = this.orderStatus.filter(orderStatus => orderStatus.name == status);
        if (item.length == 1) {
            return item[0].translation;
        }
        return null;
    }

    showOrderConfirmation(orderId: string) {
        this.orders.filter(order => order.id === orderId).map(order => {
            order.loadingConfirmation = true;
        });
        this.ordersService.getHTMLConfirmation(this.customerId || '', orderId, !this.userService.hasLoggedInUserAccess({ type: 'prices', customerId: this.customerId ?? '' }))
            .subscribe(
                r => {
                    if (r.result?.orders?.data[0].htmlConfirmationCustomer) {
                        const blob = new Blob([r.result?.orders?.data[0].htmlConfirmationCustomer], {type: "text/html"});
                        const url = window.URL.createObjectURL(blob);
                        this.orderConfirmation = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                    }
                }
            );
    }

    putOrderToCart(orderId: string) {
        this.orders.filter(order => order.id === orderId).map(order => {
            order.loadingPutOrderToCart = true;
        });
        let articles: { amount: number, articleNumber: string }[] = [];

        function setArticle(a: { amount: number, articleNumber: string }) {
            if (articles.filter(x => x.articleNumber == a.articleNumber)[0]) {
                articles = articles.map(x => {
                    if (x.articleNumber == a.articleNumber) {
                        return {amount: (x.amount + a.amount), articleNumber: a.articleNumber};
                    }
                    return x;
                })
            } else {
                articles.push({amount: a.amount, articleNumber: a.articleNumber});
            }
        }

        this.store.select(state => state.shoppingCart?.items?.articles)
            .pipe(take(1))
            .subscribe(
                r => {
                    if (r) {
                        r.map(a => {
                            setArticle({amount: a.amount, articleNumber: a.articleNumber});
                        });
                    }

                }
            )
        this.orders.filter(order => order.id === orderId).map(order => {
            order.items.articles?.map(
                (a: { amount: any; articleNumber: any; }) => {
                    setArticle({amount: a.amount, articleNumber: a.articleNumber});
                }
            )
        });
        if (articles.length > 0) {
            this.store.dispatch(setShoppingCartArticles({jsonId: 'customerOrders.putOrderToCart', articles}));
        }

        setTimeout(() => {
            this.router.navigate(['/checkout']).then();
        }, 200);

    }

    closeOrderConfirmation() {
        this.orders.map(order => {
            order.loadingConfirmation = false;
        });
        this.orderConfirmation = undefined;
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    protected readonly NumberFormat = NumberFormat;
}
